import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import * as C from "@sp-crm/core";
import {
    CommunityId,
    EmailTemplateId,
    emailTokens,
    FileEntityId,
    FileId,
    genInternalId,
    getRequestAvailabilityBlockHtml,
    IContact,
    InternalId,
    IPlacement,
    PlacementId,
    requestAvailabilityBlockId,
    TokenPrefix,
} from "@sp-crm/core";
import { PlacementEditor } from "components/editor/editor";
import { Template } from "components/editor/template";
import { Attachments } from "components/messages/attachments";
import { BccMyselfCheckbox } from "components/messages/bcc-myself-checkbox";
import { MandatoryEmailToken } from "components/messages/mandatory-token";
import { MessageComposeBasic } from "components/messages/message-compose-basic";
import { RecipientsField } from "components/messages/recipients-field";
import { SmtpMissingWarningBanner } from "components/messages/smtp-missing-warning-banner";
import { Checkbox } from "components/ui/checkbox";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Input } from "components/ui/input";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { TooltipLabel } from "components/ui/tooltip-label";
import {
    AdvancedSearchEntityType,
    GetClientForIntakeEmailQuery,
} from "generated/graphql";
import { produce } from "immer";
import * as React from "react";
import { handleEvent } from "util/user-events";
import { RequestState } from "../../../../constants/loading";
import { defaultRequestDescription } from "../../../../store/reducers/status";
import { RequestDescription } from "../../../../types/request-description";
import { BottomPanel } from "../email-components/bottom-panel";
import {
    closeWhileSendingConfirmation,
    discardEmailConfirmation,
    sendEmptySubjectConfirmation,
} from "../email-components/confirm-dialog";
import { SelectCommunities } from "../email-components/select-communities";
import {
    buildCommunityRecipient,
    CommunityRecipient,
    SelectCommunityRecipients,
} from "../email-components/select-community-recipients";
import { ClientReferralPdfCustomization, defaultClientPDFOptions } from "./customize";
import {
    ClientEmailAudience,
    ClientEmailVariant,
    emailDialogHeaderText,
    offersIntakeAttachment,
} from "./email-audience";

export interface ClientIntakeEmailDialogProps {
    audience: ClientEmailAudience;
    bulkEmailEnabled: boolean;
    clientCommunities: GetClientForIntakeEmailQuery["getClient"]["clientCommunities"];
    placement: IPlacement;
    clientDisclosureFileId: FileId | null;
    clientId: PlacementId;
    disclosureEnabled: boolean;
    files: { entityName: string; files: C.IFile[] }[];
    inClientIntakeWorkflow: boolean;
    includeDisclosure: boolean;
    initialTemplateId?: EmailTemplateId | null;
    loadFiles: (entityId: FileEntityId) => void;
    mode: C.ClientIntakeMessageMode;
    onDismiss: () => void;
    recipients?: string[];
    send: (eventId: InternalId, data: C.ClientIntakeMessage) => void;
    userPreferences: C.UserPreferences;
    requests: { [key: string]: RequestDescription };
    userContacts: IContact[];
    includeCommunityAvailability: boolean;
}

interface ClientIntakeEmailDialogState {
    message: C.MessageOptions;
    recipientSuggestions: C.IContact[];
    communityRecipients: CommunityRecipient[];
    options: C.ClientIntakeCustomizationOptions;
    dirtyRecipientList: boolean;
    eventId: InternalId;
    includeIntake: boolean;
    dirty: boolean;
    requestAvailability: boolean;
}

const communityPreserveTokens: TokenPrefix[] = ["community" as TokenPrefix];

const availabilityBlockType = "availability_block";

export class ClientIntakeEmailDialogInternal extends React.Component<
    ClientIntakeEmailDialogProps,
    ClientIntakeEmailDialogState
> {
    private editor: React.MutableRefObject<PlacementEditor> = React.createRef();

    constructor(p: ClientIntakeEmailDialogProps) {
        super(p);
        const isNotifyMode = p.mode === "notify";
        const referredCommunityIds = new Set(
            p.clientCommunities.filter(cc => cc.referred).map(cc => cc.community.id),
        );
        this.state = {
            eventId: genInternalId(),
            options: defaultClientPDFOptions(p.placement, this.props.userPreferences),
            message: this.buildMessage(p),
            recipientSuggestions: p.recipients
                ? p.recipients.map(r => ({ name: r, email1: r }) as C.IContact)
                : [],
            communityRecipients: this.props.clientCommunities
                .filter(cc => cc.relationship !== C.CommunityRelationshipType.excluded)
                .map(cc => {
                    const recipient = buildCommunityRecipient(cc);
                    if (isNotifyMode) {
                        const isChosen =
                            cc.community.id === p.placement.chosenCommunityId;
                        const wasContacted = referredCommunityIds.has(cc.community.id);
                        return {
                            ...recipient,
                            isSelected: wasContacted && !isChosen,
                        };
                    }
                    return recipient;
                }),
            dirtyRecipientList: false,
            includeIntake: offersIntakeAttachment(p) && p.inClientIntakeWorkflow,
            dirty: false,
            requestAvailability: isNotifyMode
                ? false
                : this.props.includeCommunityAvailability,
        };
        this.dismiss = this.dismiss.bind(this);
        this.handlePanelDismiss = this.handlePanelDismiss.bind(this);
        this.newEmail = this.newEmail.bind(this);
        this.sendMessage = this.sendMessage.bind(this);
        this.onSubjectChange = this.onSubjectChange.bind(this);
        this.onRecipientsChange = this.onRecipientsChange.bind(this);
        this.onBccMyselfChange = this.onBccMyselfChange.bind(this);
    }

    componentDidMount(): void {
        this.props.loadFiles(this.props.placement.id);

        this.afterEditorLoaded(() => {
            this.addAvailabilityBlockToEditor();
        });
    }

    private afterEditorLoaded(callback: () => void): void {
        const checkEditor = () => {
            if (this.editor.current?.isReady()) {
                callback();
            } else {
                setTimeout(checkEditor, 100);
            }
        };

        checkEditor();
    }

    private addAvailabilityBlockToEditor(): void {
        if (!this.props.includeCommunityAvailability) {
            return;
        }

        if (this.editor.current) {
            const wrapper = this.createAvailabilityBlock();
            this.editor.current.insertEntityAtEnd(
                availabilityBlockType,
                requestAvailabilityBlockId,
                wrapper,
            );
        }
    }

    private removeAvailabilityBlockFromEditor(): void {
        if (this.editor.current) {
            this.editor.current.removeEntitiesByType(availabilityBlockType);
        }
    }

    private requestDescription(): RequestDescription {
        return this.props.requests[this.state.eventId] || defaultRequestDescription();
    }

    private buildMessage(p: ClientIntakeEmailDialogProps): C.MessageOptions {
        return {
            recipients: p.recipients
                ? p.recipients.map(r => {
                      return { name: r, email1: r } as C.IContact;
                  })
                : [],
            subject: "",
            body: "",
            cc: [],
            bcc: [],
            from: null,
            bccMyself: this.props.userPreferences.bccMyself,
            communityId: null,
            attachments: [],
        };
    }

    private dismiss(delay = 0): void {
        const doDismiss = () => setTimeout(this.props.onDismiss.bind(this), delay);
        if (this.state.dirty) {
            (async () => {
                const result = await discardEmailConfirmation();
                if (result) {
                    doDismiss();
                }
            })();
        } else {
            doDismiss();
        }
    }

    private async handlePanelDismiss(): Promise<void> {
        if (this.requestDescription().status === RequestState.REQUESTED) {
            const proceed = await closeWhileSendingConfirmation();
            if (!proceed) {
                return;
            }
        }
        this.props.onDismiss();
    }

    private pdfCustomizationOptions(): C.ClientIntakeCustomizationOptions {
        return this.state.options;
    }

    private updateOptions(options: C.ClientIntakeCustomizationOptions): void {
        this.setState({ ...this.state, options, dirty: true });
    }

    private sendMessage() {
        const pendingImageCount = this.editor.current?.getPendingImageCount() ?? 0;
        if (pendingImageCount > 0) {
            fancyAlert(
                "Images Still Processing",
                "Please wait a moment for your pasted images to finish uploading before sending.",
            );
            return;
        }

        this.removeAvailabilityBlockFromEditor();
        const body = this.editor.current.getContent();

        const message: C.ClientIntakeMessage = {
            pdfOptions: this.state.options,
            clientId: this.props.placement.id,
            messageOptions: this.props.bulkEmailEnabled
                ? this.state.communityRecipients
                      .filter(communityRecipient => communityRecipient.isSelected)
                      .map(communityRecipient => ({
                          ...this.state.message,
                          body,
                          communityId: communityRecipient.community.id,
                          recipients: communityRecipient.recipients,
                      }))
                : [
                      {
                          ...this.state.message,
                          body,
                      },
                  ],
            includeIntake: this.state.includeIntake,
            includeDisclosureLink: this.props.includeDisclosure,
            attachments: [],
            requestAvailability: this.state.requestAvailability,
            mode: this.props.mode,
        };

        if (!this.hasRecipients()) {
            fancyAlert(
                "Error: Missing Recipients",
                "Either no community recipients are selected, or some selected communities have no associated recipients. Please Edit recipients to correct this error and then send.",
            );
            return;
        }

        if (this.props.includeDisclosure && !body.includes(emailTokens.liveLink)) {
            fancyAlert(
                "Link Placeholder Missing",
                `To send an email with a disclosure link, the message body must contain ${emailTokens.liveLink}. Please add ${emailTokens.liveLink} to the body and then send.`,
            );
            return;
        }

        const doSend = () => {
            handleEvent("message-send", {
                source: "client-intake-email",
            });
            this.props.send(this.state.eventId, message);
            this.setState({ ...this.state, dirty: false });
        };
        if (!this.state.message.subject || this.state.message.subject === "") {
            (async () => {
                const result = await sendEmptySubjectConfirmation();
                if (result) {
                    doSend();
                }
            })();
        } else {
            doSend();
        }
    }

    private hasRecipients(): boolean {
        const hasValidRecipients = () => {
            let atLeastOneCommunitySelected = false;
            let missingSomeRecips = false;

            this.state.communityRecipients.forEach(communityRecips => {
                if (missingSomeRecips) {
                    // if we're already missing some recips, we don't need
                    // to do any other checks in this loop.
                    return;
                }

                if (communityRecips.isSelected) {
                    atLeastOneCommunitySelected = true;

                    if (
                        !communityRecips.recipients ||
                        communityRecips.recipients.length === 0
                    ) {
                        // this community is selected, but it has no recips
                        missingSomeRecips = true;
                    }
                }
            });

            return atLeastOneCommunitySelected && !missingSomeRecips;
        };

        if (this.props.bulkEmailEnabled) {
            return hasValidRecipients();
        } else {
            return (
                this.state.message.recipients?.length > 0 ||
                this.state.message.cc?.length > 0
            );
        }
    }

    private updateSelectedCommunity(id: CommunityId): void {
        const doChange = () => {
            const communityRecipient: CommunityRecipient = this.props
                .inClientIntakeWorkflow
                ? (this.state.communityRecipients.find(cr => cr.community.id === id) ??
                  null)
                : null;
            const newMessage = {
                ...this.state.message,
                communityId: id,
                recipients: communityRecipient?.recipients ?? [],
            };
            const newState = {
                ...this.state,
                message: newMessage,
                recipientSuggestions: communityRecipient?.contacts ?? [],
                dirtyRecipientList: false,
                dirty: true,
            };
            this.setState(newState);
        };
        if (this.state.dirtyRecipientList) {
            (async () => {
                const result = await fancyConfirm(
                    "Change community?",
                    "You've updated TO: line; if you change communities, these changes will be lost. Are you sure you want to proceed?",
                    "Yes, I'm sure",
                    "Cancel",
                );
                if (result) {
                    doChange();
                }
            })();
        } else {
            doChange();
        }
    }

    private newEmail(): void {
        const newMessage: C.MessageOptions = {
            ...this.state.message,
            recipients: [],
        };

        this.setState({
            ...this.state,
            eventId: genInternalId(),
            message: newMessage,
            dirty: false,
        });
    }

    private attachmentForm(): JSX.Element {
        return (
            <Attachments
                scopes={this.props.files}
                selectedFileIds={this.state.message.attachments}
                onSelectFile={(fileId: FileId) => {
                    const isFileSelected =
                        this.state.message.attachments.includes(fileId);
                    this.setState({
                        message: {
                            ...this.state.message,
                            attachments: isFileSelected
                                ? this.state.message.attachments.filter(a => fileId !== a)
                                : [...this.state.message.attachments, fileId],
                        },
                    });
                }}
            />
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    private onSubjectChange(e: any) {
        const message = this.state.message;
        const newMessage = {
            ...message,
            subject: e.target.value,
        };
        this.setState({
            message: newMessage,
        });
    }

    private onBccMyselfChange(checked: boolean) {
        this.setState({
            message: { ...this.state.message, bccMyself: checked },
        });
    }

    private onRecipientsChange(contacts: IContact[]): void {
        const newMessage = {
            ...this.state.message,
            recipients: contacts,
        };
        this.setState({ message: newMessage, dirtyRecipientList: true });
    }

    private onTemplateInserted(template: Template): void {
        const newMessage: C.MessageOptions = produce(this.state.message, draft => {
            if (!draft.subject && template.subject) {
                draft.subject = template.subject;
            }

            if (Array.isArray(template.attachments)) {
                template.attachments.forEach(attachmentId => {
                    if (!draft.attachments.includes(attachmentId)) {
                        draft.attachments.push(attachmentId);
                    }
                });
            }
        });

        this.setState({ message: newMessage });
    }

    private createAvailabilityBlock(): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.style.border = "1px dotted #e5e7eb";
        wrapper.style.padding = "12px";
        wrapper.style.margin = "16px 0";
        wrapper.style.borderRadius = "4px";
        wrapper.style.backgroundColor = "#fafafa";
        wrapper.contentEditable = "false";

        const label = document.createElement("div");
        label.style.fontSize = "10px";
        label.style.color = "#9ca3af";
        label.style.fontWeight = "500";
        label.style.textTransform = "uppercase";
        label.style.letterSpacing = "0.05em";
        label.style.marginTop = "12px";
        label.innerHTML =
            "ℹ️ Auto-generated - The included content will only appear for communities who have not yet provided availability";

        const content = document.createElement("div");
        content.innerHTML = getRequestAvailabilityBlockHtml("");

        wrapper.appendChild(content);
        wrapper.appendChild(label);

        return wrapper;
    }

    private handleRequestAvailabilityChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const checked = e.target.checked;

        if (!this.editor.current) {
            return;
        }

        this.removeAvailabilityBlockFromEditor();

        if (checked) {
            this.addAvailabilityBlockToEditor();

            this.setState({
                requestAvailability: true,
                dirty: true,
            });
        } else {
            this.setState({
                requestAvailability: false,
                dirty: true,
            });
        }
    };

    private cc(): JSX.Element {
        return (
            <RecipientsField
                label="Cc"
                recipients={this.state.message.cc}
                suggestions={[
                    ...this.state.recipientSuggestions,
                    ...this.props.userContacts,
                ]}
                onChange={(contacts: IContact[]) => {
                    const newMessage = {
                        ...this.state.message,
                        cc: contacts,
                    };
                    this.setState({ message: newMessage });
                }}
            />
        );
    }

    render() {
        const variant: ClientEmailVariant = {
            mode: this.props.mode,
            audience: this.props.audience,
        };
        const headerText = emailDialogHeaderText(
            variant,
            this.props.placement.preferredClientName,
        );
        const attachLabel = `Attach PDF with Client Intake details`;
        return (
            <>
                <ResponsivePanel
                    type={PanelType.extraLarge}
                    onDismiss={this.handlePanelDismiss}
                    isOpen={true}
                    allowOutsideDismiss={false}
                    headerText={headerText}>
                    <div className="pdf-dialog-panel space-y-4">
                        <SmtpMissingWarningBanner />
                        <div className="space-y-4">
                            {this.props.bulkEmailEnabled ? (
                                <div className="space-y-2">
                                    <SelectCommunityRecipients
                                        communityRecipients={
                                            this.state.communityRecipients
                                        }
                                        onUpdateCommunityRecipients={newValues => {
                                            this.setState({
                                                communityRecipients: newValues,
                                            });
                                        }}
                                        previouslyContactedCommunities={this.props.clientCommunities
                                            .filter(cc => cc.referred)
                                            .map(cc => cc.community.id)}
                                        mode={this.props.mode}
                                    />
                                    {this.cc()}
                                </div>
                            ) : (
                                <>
                                    {this.props.audience === "communities" ? (
                                        <SelectCommunities
                                            communities={this.state.communityRecipients.map(
                                                cr => cr.community,
                                            )}
                                            message={this.state.message}
                                            updateSelectedCommunity={this.updateSelectedCommunity.bind(
                                                this,
                                            )}
                                        />
                                    ) : null}
                                    <div className="space-y-2">
                                        <RecipientsField
                                            label="To"
                                            recipients={this.state.message.recipients}
                                            suggestions={this.state.recipientSuggestions}
                                            onChange={this.onRecipientsChange}
                                        />
                                        {this.cc()}
                                    </div>
                                </>
                            )}
                        </div>

                        <Input
                            value={this.state.message.subject}
                            onChange={this.onSubjectChange}
                            placeholder="Subject"
                        />

                        <div className="my-4">
                            <MessageComposeBasic
                                ref={this.editor}
                                initialTemplateId={this.props.initialTemplateId}
                                onTemplateInserted={this.onTemplateInserted.bind(this)}
                                entityType={AdvancedSearchEntityType.Client}
                                entityId={this.props.clientId}
                                preserveTokens={communityPreserveTokens}
                                onEntityAdded={entity => {
                                    if (entity.type === availabilityBlockType) {
                                        if (!this.state.requestAvailability) {
                                            this.setState({
                                                requestAvailability: true,
                                                dirty: true,
                                            });
                                        }
                                    }
                                }}
                                onEntityRemoved={entity => {
                                    if (entity.type === availabilityBlockType) {
                                        if (this.state.requestAvailability) {
                                            this.setState({
                                                requestAvailability: false,
                                                dirty: true,
                                            });
                                        }
                                    }
                                }}
                            />
                        </div>
                        {this.props.disclosureEnabled && this.props.includeDisclosure ? (
                            this.props.clientDisclosureFileId ? (
                                <p>
                                    <MandatoryEmailToken use="disclosure" />
                                </p>
                            ) : (
                                <div className="flex space-x-4 text-red-700 my-2">
                                    <ExclamationTriangleIcon className="w-12 h-12" />
                                    <p>
                                        There is no disclosure PDF configured in your
                                        agency settings. Navigate to Agency Settings and
                                        configure a PDF to ensure recipients can
                                        acknowledge your disclosure.
                                    </p>
                                </div>
                            )
                        ) : null}
                        <div className="my-2">
                            <BccMyselfCheckbox
                                bccMyself={this.state.message.bccMyself}
                                onChange={this.onBccMyselfChange}
                            />
                        </div>

                        {this.attachmentForm()}
                        {offersIntakeAttachment(variant) ? (
                            <>
                                <div className="mt-4">
                                    <Checkbox
                                        label={attachLabel}
                                        checked={this.state.includeIntake}
                                        onChange={(
                                            e: React.ChangeEvent<HTMLInputElement>,
                                        ) =>
                                            this.setState({
                                                includeIntake: e.target.checked,
                                            })
                                        }
                                    />
                                </div>
                                {this.props.includeCommunityAvailability ? (
                                    <Checkbox
                                        label={
                                            <div className="flex items-center space-x-2">
                                                <span>
                                                    Include community availability request
                                                </span>
                                                <TooltipLabel
                                                    align="center"
                                                    tooltip={
                                                        "Include a link to ask communities to report their availability if they have not already."
                                                    }
                                                />
                                            </div>
                                        }
                                        checked={this.state.requestAvailability}
                                        onChange={this.handleRequestAvailabilityChange}
                                    />
                                ) : null}
                                {this.state.includeIntake ? (
                                    <ClientReferralPdfCustomization
                                        options={this.pdfCustomizationOptions()}
                                        callback={o => this.updateOptions(o)}
                                    />
                                ) : null}
                            </>
                        ) : null}
                        <div className="pdf-dialog-panel--section">
                            <BottomPanel
                                eventId={this.state.eventId}
                                requestDescription={this.requestDescription()}
                                dismiss={this.dismiss}
                                sendMessage={this.sendMessage}
                                newMessage={this.newEmail}
                                sendEnabled={
                                    !this.props.includeDisclosure ||
                                    !!this.props.clientDisclosureFileId
                                }
                            />
                        </div>
                    </div>
                </ResponsivePanel>
            </>
        );
    }
}
